import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { FileText, FileSpreadsheet, Scale } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AccountingTrialBalance() {
  useOutletContext();
  const [range, setRange] = useState({ date_from: '', date_to: '' });
  const [onlyMov, setOnlyMov] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { only_with_movements: onlyMov };
      if (range.date_from) params.date_from = range.date_from;
      if (range.date_to) params.date_to = range.date_to;
      const r = await axios.get(`${BACKEND_URL}/api/accounting/trial-balance`, { params, withCredentials: true });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cargar Sumas y Saldos'); } finally { setLoading(false); }
  }, [range, onlyMov]);

  useEffect(() => { load(); }, [load]);

  const buildUrl = (ext) => {
    const url = new URL(`${BACKEND_URL}/api/accounting/trial-balance.${ext}`);
    url.searchParams.set('only_with_movements', onlyMov);
    if (range.date_from) url.searchParams.set('date_from', range.date_from);
    if (range.date_to) url.searchParams.set('date_to', range.date_to);
    return url.toString();
  };

  return (
    <div className="space-y-6" data-testid="accounting-trial-balance-page">
      <div className="flex items-center gap-3">
        <Scale className="w-7 h-7 text-emerald-600" />
        <div>
          <h1 className="text-2xl font-bold">Balance de Sumas y Saldos</h1>
          <p className="text-sm text-muted-foreground">Sumas del Debe/Haber y saldos por cuenta</p>
        </div>
      </div>

      <Card><CardContent className="pt-6">
        <div className="flex flex-wrap items-end gap-4">
          <div><Label className="text-xs">Desde</Label><Input type="date" value={range.date_from} onChange={e => setRange({ ...range, date_from: e.target.value })} data-testid="tb-date-from" /></div>
          <div><Label className="text-xs">Hasta</Label><Input type="date" value={range.date_to} onChange={e => setRange({ ...range, date_to: e.target.value })} data-testid="tb-date-to" /></div>
          <div className="flex items-center gap-2 pb-2">
            <Switch id="onlymov" checked={onlyMov} onCheckedChange={setOnlyMov} data-testid="tb-only-movements" />
            <Label htmlFor="onlymov" className="text-sm">Solo cuentas con movimientos</Label>
          </div>
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" onClick={() => window.open(buildUrl('pdf'), '_blank')} data-testid="tb-pdf"><FileText className="w-4 h-4 mr-2" />PDF</Button>
            <Button variant="outline" onClick={() => window.open(buildUrl('xlsx'), '_blank')} data-testid="tb-excel"><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</Button>
          </div>
        </div>
      </CardContent></Card>

      {loading ? <p className="text-center py-8 text-muted-foreground">Cargando…</p> : data && (
        <Card><CardContent className="pt-6">
          <div className="mb-3 flex items-center gap-3 flex-wrap">
            {data.balanced
              ? <Badge className="bg-emerald-500" data-testid="tb-balanced">✔ Balanceado</Badge>
              : <Badge className="bg-rose-500" data-testid="tb-not-balanced">⚠ NO BALANCEA</Badge>}
            <span className="text-xs text-muted-foreground">Saldo = Debe − Haber · positivo = deudor, <span className="text-rose-600">negativo = acreedor</span></span>
          </div>
          <Table data-testid="tb-table">
            <TableHeader><TableRow>
              <TableHead className="w-32">Código</TableHead><TableHead>Cuenta</TableHead>
              <TableHead className="text-right">Debe</TableHead><TableHead className="text-right">Haber</TableHead>
              <TableHead className="text-right">Saldo</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.rows.map((r) => (
                <TableRow key={r.code}>
                  <TableCell className="font-mono text-xs">{r.code}</TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell className="text-right">{fmtMoney(r.debit)}</TableCell>
                  <TableCell className="text-right">{fmtMoney(r.credit)}</TableCell>
                  <TableCell className={`text-right font-medium ${r.saldo < 0 ? 'text-rose-600' : ''}`}>{fmtMoney(r.saldo)}</TableCell>
                </TableRow>
              ))}
              {data.rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">Sin movimientos</TableCell></TableRow>}
              <TableRow className="bg-muted font-bold border-t-2">
                <TableCell colSpan={2}>TOTALES</TableCell>
                <TableCell className="text-right">{fmtMoney(data.totals.debit)}</TableCell>
                <TableCell className="text-right">{fmtMoney(data.totals.credit)}</TableCell>
                <TableCell className="text-right">{fmtMoney(data.totals.saldo)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent></Card>
      )}
    </div>
  );
}
